import { loginRequest } from "@/services/auth/msalConfig";
import { useMsal } from "@azure/msal-react";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { useState } from "react";

// Brand colours from the app icon (inspect-frontend-client/scripts/render-icons.py).
// The icon's #009c9c is too light for white text (3.3:1), so text and the button
// use a darker teal that clears 4.5:1.
const TEAL = "#009c9c";
const TEAL_TEXT = "#007a7a";
const NAVY = "#163578";

export function Login() {
	const { instance, inProgress } = useMsal();
	const [isLoggingIn, setIsLoggingIn] = useState(false);
	const busy = isLoggingIn || inProgress !== "none";

	const handleLogin = () => {
		if (inProgress !== "none") return;

		setIsLoggingIn(true);
		instance.loginRedirect(loginRequest).catch((e) => {
			console.error(e);
			setIsLoggingIn(false);
		});
	};

	return (
		<div className="relative flex min-h-dvh flex-col bg-slate-50 dark:bg-slate-950">
			{/* A soft teal glow behind the card, so the page isn't a flat field. */}
			<div
				aria-hidden
				className="pointer-events-none absolute inset-0 opacity-60 dark:opacity-40"
				style={{
					background: `radial-gradient(60rem 30rem at 50% 30%, ${TEAL}26, transparent 70%)`,
				}}
			/>

			<main className="relative flex flex-1 items-center justify-center px-4 py-12">
				<div className="w-full max-w-md">
					<div className="rounded-xl border border-slate-200 bg-white p-8 shadow-xl dark:border-slate-800 dark:bg-slate-900">
						<div className="flex flex-col items-center text-center">
							<img
								src="/icons/icon.svg"
								alt=""
								width={64}
								height={64}
								className="mb-4 h-16 w-16 rounded-2xl shadow-md"
							/>
							<div className="flex items-center gap-2">
								<span className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
									InspectTec
								</span>
								<span
									className="rounded-md px-2 py-0.5 text-xs font-semibold uppercase tracking-wider text-white"
									style={{ backgroundColor: NAVY }}
								>
									Admin
								</span>
							</div>
							<h1 className="mt-4 text-lg font-semibold text-slate-800 dark:text-slate-100">
								Administration Portal
							</h1>
							<p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
								For InspectTec staff. Sign in with your company
								Microsoft account.
							</p>
						</div>

						<button
							type="button"
							onClick={handleLogin}
							disabled={busy}
							className="mt-6 flex w-full items-center justify-center gap-2 rounded-md px-4 py-2.5 font-medium text-white shadow-sm transition hover:brightness-110 focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
							style={{
								backgroundColor: TEAL_TEXT,
								outlineColor: TEAL_TEXT,
							}}
						>
							<ShieldCheck className="h-4 w-4" aria-hidden />
							{busy ? "Signing in..." : "Sign in with Microsoft"}
						</button>
					</div>

					{/* For a customer who landed here by mistake. */}
					<div className="mt-4 rounded-lg border border-slate-200 bg-white/70 px-4 py-3 text-center text-sm text-slate-600 dark:border-slate-800 dark:bg-slate-900/60 dark:text-slate-300">
						Looking for your company's InspectTec?{" "}
						<a
							href="https://app.inspecttec.com"
							className="inline-flex items-center gap-1 font-medium text-[#007a7a] hover:underline dark:text-[#3cc8c8]"
						>
							Sign in at app.inspecttec.com
							<ArrowRight className="h-3.5 w-3.5" aria-hidden />
						</a>
					</div>
				</div>
			</main>

			<footer className="relative pb-6 text-center text-xs text-slate-400 dark:text-slate-500">
				© {new Date().getFullYear()} InspectTec ·{" "}
				<a
					href="https://help.inspecttec.com"
					className="hover:underline"
				>
					Help
				</a>
			</footer>
		</div>
	);
}
